import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AlertTriangle, ArrowDownCircle } from 'lucide-react';
import { formatApiError } from '@/lib/validators';
import { SidePane } from '@/components/data';

/**
 * Every ink in the store, in kilograms.
 *
 * The same four columns as the board ledger, because ink is the same kind of
 * thing: bought by weight, costed by weight, wasted by weight. What it is not
 * is a count of tins — half a tin is the normal state of an ink on a press, so
 * the number that moves is a weight and the tin size is only how a delivery
 * note gets converted.
 *
 * Unlike board, nothing computes ink consumption for us — no imposition prices
 * a run in grams of Pantone 485 — so the ledger carries its own Issue action.
 * That is the whole point of the screen: without somewhere to record ink going
 * out to a job, the closing weight is a number nobody has any reason to trust.
 */

const kg = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const HEADS = ['Ink', 'Press', 'Shade', 'Opening', 'Purchased', 'Consumed', 'Closing', 'Location', ''];

const FILTERS = [['all', 'All ink'], ['in_stock', 'In stock'], ['negative', 'Negative stock']];

const PRESS_LABELS = {
  offset: 'Offset',
  uv: 'UV',
  flexo: 'Flexo',
  screen: 'Screen',
  water_based: 'Water-based',
  solvent: 'Solvent',
};

const EMPTY_ISSUE = { weight_kg: '', tins: '', order_id: '', remarks: '' };

const pressOf = (ink) => PRESS_LABELS[ink?.type] || ink?.type || '—';
const shadeOf = (ink) => (ink?.pantone ? `Pantone ${ink.pantone}` : ink?.shade || '—');

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-lg font-bold tabular-nums sm:text-xl ${tone || 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

function Flags({ row }) {
  return (
    <>
      {row.negative && <Badge className="ml-1 bg-red-50 text-[9px] text-red-700">negative</Badge>}
      {row.below_minimum && <Badge className="ml-1 bg-amber-50 text-[9px] text-amber-700">low</Badge>}
    </>
  );
}

function InkCard({ row, onIssue }) {
  return (
    <li
      data-testid={`ink-row-${row.item_id}`}
      className={cn(
        'rounded-lg border bg-white p-3 shadow-card',
        row.negative ? 'border-l-4 border-slate-200 border-l-red-500'
          : row.below_minimum ? 'border-l-4 border-slate-200 border-l-amber-500'
            : 'border-slate-200'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {row.name}
            {row.ink?.batch_no && <span className="ml-1 text-[10px] text-slate-400">#{row.ink.batch_no}</span>}
          </p>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {pressOf(row.ink)}
            {row.ink?.pantone || row.ink?.shade ? ` · ${shadeOf(row.ink)}` : ''}
            {row.location ? ` · ${row.location}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className={cn(
            'text-xl font-bold leading-none tabular-nums',
            row.negative ? 'text-red-600' : row.below_minimum ? 'text-amber-600' : 'text-slate-900'
          )}>
            {kg(row.closing_kg)}
          </p>
          <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">closing kg</p>
        </div>
      </div>

      <dl className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2 text-center">
        {[['Opening', kg(row.opening_kg), 'text-slate-600'],
          ['Purchased', kg(row.purchased_kg), 'text-emerald-700'],
          ['Consumed', kg(row.consumed_kg), 'text-slate-600']].map(([label, value, tone]) => (
          <div key={label}>
            <dt className="text-[10px] uppercase tracking-wider text-slate-400">{label}</dt>
            <dd className={`text-sm font-medium tabular-nums ${tone}`}>{value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span><Flags row={row} /></span>
        <Button size="sm" variant="outline" className="h-9 px-3 text-[13px]"
          onClick={() => onIssue(row)} data-testid={`issue-ink-${row.item_id}`}>
          <ArrowDownCircle className="mr-1 h-3.5 w-3.5" />
          Issue
        </Button>
      </div>
    </li>
  );
}

export default function InkLedger() {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all'); // all | negative | in_stock
  const [issuing, setIssuing] = useState(null);
  const [form, setForm] = useState(EMPTY_ISSUE);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/inventory/ink/ledger', {
        params: {
          negative_only: filter === 'negative',
          in_stock_only: filter === 'in_stock',
        },
      });
      setData(res.data);
    } catch {
      toast.error('Could not load the ink ledger');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openIssue = (row) => { setForm(EMPTY_ISSUE); setIssuing(row); };
  const closeIssue = () => { setIssuing(null); setForm(EMPTY_ISSUE); };

  const submitIssue = async () => {
    const weight = parseFloat(form.weight_kg);
    const tins = parseFloat(form.tins);
    if (!(weight > 0) && !(tins > 0)) {
      toast.error('Give a weight in kg, or a number of tins');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/inventory/ink/issue', {
        item_id: issuing.item_id,
        ...(weight > 0 ? { weight_kg: weight } : { tins }),
        order_id: form.order_id || undefined,
        remarks: form.remarks,
      });
      // the tin has gone whether or not the books agreed, so a negative close is
      // reported rather than treated as a failed movement
      if (res.data.negative) {
        toast.warning(`${issuing.name} is now ${kg(res.data.stock_after)} kg — more went out than the store showed`);
      } else {
        toast.success(`Issued from ${issuing.name}`);
      }
      closeIssue();
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not record the issue'));
    }
    setSubmitting(false);
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const emptyMessage = 'No ink on the books yet — add a stock item with an ink spec.';
  const tinKg = issuing?.ink?.tin_kg || 0;

  return (
    <div className="space-y-3" data-testid="ink-ledger">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
        <Stat label="Opening (kg)" value={kg(data.totals.opening)} />
        <Stat label="Purchased (kg)" value={kg(data.totals.purchased)} />
        <Stat label="Consumed (kg)" value={kg(data.totals.consumed)} />
        <Stat label="Closing (kg)" value={kg(data.totals.closing)}
          tone={data.totals.closing < 0 ? 'text-red-600' : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="grid w-full grid-cols-3 gap-1.5 sm:flex sm:w-auto">
          {FILTERS.map(([value, label]) => (
            <Button key={value} size="sm" variant={filter === value ? 'default' : 'outline'}
              className="h-10 px-2 text-[13px] sm:h-8 sm:px-3"
              onClick={() => setFilter(value)} data-testid={`ink-filter-${value}`}>
              {label}
            </Button>
          ))}
        </div>
        {data.negative_count > 0 && (
          <span className="flex items-start gap-1 text-xs text-red-600" data-testid="ink-negative-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {data.negative_count} ink{data.negative_count > 1 ? 's have' : ' has'} gone negative —
              more was issued than the store shows
            </span>
          </span>
        )}
      </div>

      {isMobile ? (
        data.rows.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-white px-3 py-10 text-center text-sm text-slate-400">
            {emptyMessage}
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="ink-cards">
            {data.rows.map(row => <InkCard key={row.item_id} row={row} onIssue={openIssue} />)}
          </ul>
        )
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs" data-testid="ink-table">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                {HEADS.map(h => (
                  <th key={h} className="whitespace-nowrap px-2.5 py-2 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={9} className="px-2 py-10 text-center text-slate-400">{emptyMessage}</td></tr>
              ) : data.rows.map(row => (
                <tr key={row.item_id} className="border-t border-slate-200"
                  data-testid={`ink-row-${row.item_id}`}>
                  <td className="px-2.5 py-2">
                    {row.name}
                    {row.ink?.batch_no && (
                      <span className="ml-1 text-[10px] text-slate-400">#{row.ink.batch_no}</span>
                    )}
                  </td>
                  <td className="px-2.5 py-2 text-slate-500">{pressOf(row.ink)}</td>
                  <td className="px-2.5 py-2 text-slate-500">{shadeOf(row.ink)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{kg(row.opening_kg)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-emerald-700">{kg(row.purchased_kg)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-slate-600">{kg(row.consumed_kg)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums font-medium">
                    <span className={row.negative ? 'text-red-600' : row.below_minimum ? 'text-amber-600' : ''}>
                      {kg(row.closing_kg)}
                    </span>
                    <Flags row={row} />
                  </td>
                  <td className="px-2.5 py-2 text-slate-500">{row.location || '—'}</td>
                  <td className="px-2.5 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openIssue(row)}
                      data-testid={`issue-ink-${row.item_id}`}
                      className="rounded px-1.5 py-0.5 text-[11px] font-medium text-brand-blue hover:bg-blue-50"
                    >
                      Issue
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SidePane
        open={!!issuing}
        onClose={closeIssue}
        overlay
        title={`Issue ${issuing?.name || 'ink'}`}
        subtitle={issuing ? `${kg(issuing.closing_kg)} kg on the shelf` : undefined}
        width="24rem"
        footer={
          <>
            <Button variant="outline" className="h-12 px-4 text-sm sm:h-8" onClick={closeIssue}>Cancel</Button>
            <Button disabled={submitting} onClick={submitIssue} data-testid="confirm-issue-ink"
              className="h-12 flex-1 bg-brand-blue text-base font-semibold text-white hover:bg-brand-blue-dark sm:h-8 sm:flex-none sm:text-sm">
              {submitting ? 'Recording…' : 'Record issue'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Weight (kg)</span>
              <Input type="number" inputMode="decimal" data-testid="issue-ink-weight"
                className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.weight_kg}
                onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value, tins: '' }))} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">or Tins</span>
              <Input type="number" inputMode="decimal" data-testid="issue-ink-tins"
                className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.tins}
                disabled={!tinKg}
                onChange={e => setForm(f => ({ ...f, tins: e.target.value, weight_kg: '' }))} />
            </label>
          </div>
          <p className="text-[11px] text-slate-400">
            {tinKg
              ? `A tin of this ink is ${kg(tinKg)} kg.`
              : 'No tin size recorded for this ink — issue it by weight.'}
          </p>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Order</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="Order it went to"
              value={form.order_id}
              onChange={e => setForm(f => ({ ...f, order_id: e.target.value }))} />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Remarks</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="Machine, run, who took it…"
              value={form.remarks}
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
          </label>
        </div>
      </SidePane>
    </div>
  );
}
